import { ButtonColor, ButtonSize, Option } from '@code/innovance-react-ui-kit';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Customer,
  Document,
  LoanDocument,
  LoanDocumentPageValidation,
} from 'src/front/api/apiInstance';
import { ReferenceDataType, UploadType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';
import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import AsyncButton from 'src/front/components//AsyncButton';
import { UploadDocumentFormData } from 'src/front/components/document/UploadDocumentModal';
import { Container, FormRow, Subtitle, Title, WarningText } from 'src/front/components/FormStyles';
import { useCustomerSearchState } from 'src/front/contexts/LoanApplicationContext';
import UploadDocument from 'src/front/pages/applications/card-entry/document-entry/UploadDocument';
import uid from 'uid';
import IdentityEntry from './IdentityEntry';
import { IdentityFormData } from './IdentityModal';

interface Props {
  goToNextStep: (customerId?: string) => void;
}

const defaultIdentityValue = {
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  expireDate: null,
  issuedBy: '',
  issueCountry: null,
  unitCode: '',
};

interface Identity extends IdentityFormData {
  isNew?: boolean;
}

type UploadedDocument = UploadDocumentFormData;

const DocumentEntry: FC<Props> = ({ goToNextStep }) => {
  const { t } = useTranslation();

  const { customerId } = useCustomerSearchState();
  const [warning, setWarning] = useState<string>();
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [identityTypes, setIdentityTypes] = useState<Option[]>([]);
  const [isLoading, setIsloading] = useState<boolean>(true);
  const [documentPageNumber, setDocumentPageNumber] = useState<number>(1);
  const [hasDocumentNextPage, setHasDocumentNextPage] = useState<boolean>(false);

  const documentTypes = usePRM(ReferenceDataType.LOANAPPUPLOADDOCUMENTTYPE);

  useEffect(() => {
    (async () => {
      const { data } = await LoanDocument.getLoanDocumentType();

      if (data.result === 'OK') {
        setIdentityTypes(data.loanDocumentTypeList.map(optionFormat));
      }
    })();
  }, []);
  const handleSubmit = async (): Promise<void> => {
    const hasIdentity = identities.length > 0;

    if (!hasIdentity) {
      setWarning('cardEntry_documentEntry_noDocumentInfo_errorMessage');
    } else {
      setWarning(undefined);
      const { data } = await LoanDocumentPageValidation.validateLoanDocumentPageData({
        documentIdentityDataList: identities.map((identity) => ({
          ...(identity.isNew ? {} : { customerIdentityId: identity.id }),
          identityType: dictionaryLineFormat(identity.identityType),
          identityDocumentSerialCode: identity.identityDocumentSerialCode,
          identityDocumentSerialNumber: identity.identityDocumentSerialNumber,
          issueDate: formatDate(identity.issueDate),
          expireDate: formatDate(identity.expireDate),
          issuedBy: identity.issuedBy,
          unitCode: identity.unitCode,
          isNew: identity.isNew,
        })),
      });
      if (data.result === 'OK') {
        goToNextStep();
      }
    }
  };

  const loadIdentities = useCallback(async (): Promise<void> => {
    try {
      if (customerId) {
        const { data } = await Customer.getCustomerIdentities({ customerId });

        const identityList =
          data?.customerIdentityList?.map((identity) => ({
            ...defaultIdentityValue,
            identityDocumentSerialCode: identity.series,
            identityDocumentSerialNumber: identity.serialCode,
            issueDate: convertStringToDate(identity.issueDate),
            identityType: identityTypes.find((type) => type.value === identity.documentType) || {
              value: identity.documentType,
              label: identity.documentType,
            },
            id: identity.customerIdentityId,
            issuedBy: identity.issuedBy,
            unitCode: identity.unitCode || '',
            isNew: false,
          })) || [];
        setIdentities(identityList);
      }
    } catch (error) {
      // TODO (ozgunbal): show error on modal or handle with error boundary
    }
  }, [customerId, identityTypes]);

  const loadDocuments = useCallback(
    async (pageNumber?: number): Promise<void> => {
      try {
        const { data } = await Document.getDocuments({
          customerId: customerId || '',
          documentType: 'PS,FT,PT',
          rowCountPerPage: 5,
          pageNumber: pageNumber || 1,
          refresh: !pageNumber,
          uploadType: UploadType.IDENTITY,
        });

        const uploadedDocumentList =
          data.documentList?.map((document) => ({
            documentType: documentTypes.find((type) => type.value === document.documentType) || {
              value: document.documentType,
              label: document.documentType,
            },
            explanation: document.explanation,
            isOldDocument: document.isOldDocument,
            documentId: document.documentId,
            createDate: document.createDate,
            documentName: document.documentName,
            filePath: document.physicalFileName,
          })) || [];

        setDocuments(uploadedDocumentList);
        setHasDocumentNextPage(data.hasNextPage);
        setDocumentPageNumber(pageNumber || 1);
      } catch (error) {
        // TODO (ozgunbal): show error on modal or handle with error boundary
        setDocuments([]);
      }
    },
    [customerId, documentTypes]
  );

  const loadFormFields = useCallback(async (): Promise<void> => {
    try {
      const { data: response } = await LoanDocumentPageValidation.getLoanDocumentPageData();
      if (response.result === 'OK' && response.pageData) {
        const { documentIdentityDataList } = response.pageData;

        const identityList =
          documentIdentityDataList?.map((identity) => ({
            id: identity.customerIdentityId || uid(),
            identityType: optionFormat(identity.identityType),
            identityDocumentSerialCode: identity.identityDocumentSerialCode || '',
            identityDocumentSerialNumber: identity.identityDocumentSerialNumber || '',
            issueDate: convertStringToDate(identity.issueDate),
            expireDate: convertStringToDate(identity.expireDate),
            issuedBy: identity.issuedBy || '',
            unitCode: identity.unitCode || '',
            isNew: identity.isNew,
          })) || [];

        setIdentities(identityList);

        await loadDocuments();
      } else if (customerId) {
        await Promise.all([loadIdentities(), loadDocuments()]);
      }
      setIsloading(false);
    } catch (err) {
      // TODO (ozgunbal): show error on modal or handle with error boundary
    }
  }, [customerId, loadDocuments, loadIdentities]);

  useEffect(() => {
    if (identityTypes.length > 0) {
      loadFormFields();
    }
  }, [identityTypes.length, loadFormFields]);
  return (
    <Container>
      <Title>
        <Trans i18nKey="loanApp_documentEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="loanApp_documentEntry_table1_subtitle" />
      </Subtitle>
      <IdentityEntry
        identities={identities}
        identityTypes={identityTypes}
        isLoading={isLoading}
        setIdentities={setIdentities}
      />
      <Subtitle>
        <Trans i18nKey="loanApp_documentEntry_table2_subtitle" />
      </Subtitle>
      <UploadDocument
        customerId={customerId}
        documents={documents}
        documentTypes={documentTypes}
        hasNextPage={hasDocumentNextPage}
        isLoading={isLoading}
        loadDocuments={loadDocuments}
        pageNumber={documentPageNumber}
        setDocuments={setDocuments}
      />
      <WarningText>
        <Trans i18nKey={warning} />
      </WarningText>
      <FormRow>
        <AsyncButton
          color={ButtonColor.Primary}
          onClick={handleSubmit}
          size={ButtonSize.Medium}
          text={t('general_continue_button')}
        />
      </FormRow>
    </Container>
  );
};

export default DocumentEntry;
